/**
 * 条件確認画面（ConditionCheck）
 *
 * MLMタイトルの昇格条件・維持条件の達成状況を表示する画面。
 *
 * ## 主な機能
 * - バイナリー昇格条件の表示（単月/連月実績、育成条件）
 * - ユニレベル昇格条件の表示（累積課金額、育成条件）
 * - 維持条件の表示（全タイトル分）
 * - 複数ポジション対応（メイン/サブポジション切替）
 * - 条件データのキャッシュ機能
 *
 * ## タイトル体系
 * - SPC(1) → FC(2) → BC(3) → IC(4) → SLC(5) → NC(6) → SNC(7) → GC(8)
 * - バイナリー: 単月ルート or 連月ルートで昇格可能
 * - ユニレベル: IC(4)以上が対象、累積ルートで昇格可能
 *
 * ## 表示されるタイトル
 * - 最高タイトル: バックエンドで確定済みのタイトル
 * - 暫定タイトル: 現在の実績で達成している最高タイトル（最高タイトル以上）
 * - 維持タイトル: 暫定タイトルから維持条件の達成状況を加味したタイトル
 */
import Footer from 'components/Footer'
import Header from 'components/Header'
import * as React from 'react'
import { Badge, Card } from 'react-bootstrap'
import { Position } from 'api/common/types'
import { PositionNumber } from 'api/common/constants'
import { activePositionsAPI } from 'api/position'
import {
  TitleCondition,
  BinaryPromotionResponse,
  UnilevelPromotionResponse,
  MaintenanceConditionResponse,
  getTitleConditions,
  getBinaryPromotionConditions,
  getUnilevelPromotionConditions,
  getMaintenanceConditions,
} from 'api/condition'
import {
  TitleCode,
  TITLE_ID_TO_CODE,
  TITLE_INFO_MAP,
  FIRST_UNILEVEL_TITLE_ID,
} from 'constants/title'
import { calculateMaintenanceTitle, parseFormattedAmount } from 'utilities/conditionJudgment'

// ============================================
// 型定義
// ============================================

/** メインタブの種類 */
type MainTab = 'promotion' | 'maintenance'

/** サブタブの種類（昇格条件内） */
type SubTab = 'binary' | 'unilevel'

/** 条件の達成状態 */
type ConditionStatus = 'achieved' | 'pending' | 'neutral'

// 共通定数からエイリアス
const titleIdToCode = TITLE_ID_TO_CODE
const titleInfoMap = TITLE_INFO_MAP

// ============================================
// 定数
// ============================================

/** 連月判定開始年 */
const CONSECUTIVE_MONTH_START_YEAR = 2026

/** 連月判定開始月（2月から表示開始） */
const CONSECUTIVE_MONTH_START_MONTH = 2

/**
 * 連月実績を表示すべきかどうかを判定
 * 2026年2月以降のみ表示（JST基準）
 */
const shouldShowConsecutiveMonth = (): boolean => {
  // JSTで現在日時を取得
  const nowJST = new Date().toLocaleString('en-US', { timeZone: 'Asia/Tokyo' })
  const jstDate = new Date(nowJST)
  const year = jstDate.getFullYear()
  const month = jstDate.getMonth() + 1 // 0-indexed -> 1-indexed
  return year > CONSECUTIVE_MONTH_START_YEAR ||
    (year === CONSECUTIVE_MONTH_START_YEAR && month >= CONSECUTIVE_MONTH_START_MONTH)
}

/** 左右系列の条件データ（バイナリー用） */
interface LRConditionData {
  leftValue: string
  rightValue: string
  leftShortage?: string
  rightShortage?: string
  leftStatus: 'ok' | 'ng'
  rightStatus: 'ok' | 'ng'
}

/** 単一値の条件データ（ユニレベル用） */
interface SingleConditionData {
  value: string
  shortage?: string
  status: 'ok' | 'ng'
}

/** 条件行データ */
interface ConditionRowData {
  label: string
  requirement: string
  lr?: LRConditionData
  single?: SingleConditionData
}

/** 条件グループデータ（単月実績/連月実績/累積実績など） */
interface ConditionGroupData {
  title: string
  rows: ConditionRowData[]
}

/** 育成条件行データ */
interface CultivationRowData {
  label: string
  requirement?: string
  lr?: LRConditionData
  single?: SingleConditionData
}

/** タイトル別条件データ（アコーディオン1つ分） */
interface TitleConditionData {
  titleCode: TitleCode
  status: ConditionStatus
  groups: ConditionGroupData[]
  cultivation?: {
    title: string
    rows: CultivationRowData[]
  }
  note?: string
}

// ============================================
// ユーティリティ関数
// ============================================

/**
 * 金額をフォーマット（適切な単位で表示、切り捨て）
 *
 * @example
 * formatAmount(123456789) // "1億2,345万6,789円"
 * formatAmount(12345678)  // "1,234万5,678円"
 * formatAmount(3456)      // "3,456円"
 */
const formatAmount = (amount: number): string => {
  if (amount < 0) {
    return `-${formatAmount(Math.abs(amount))}`
  }

  // カンマ区切りフォーマット
  const formatWithComma = (n: number): string => n.toLocaleString('ja-JP')

  const oku = Math.floor(amount / 100000000) // 億
  const man = Math.floor((amount % 100000000) / 10000) // 万
  const remainder = amount % 10000 // 残り（円）

  // 1億以上: 億+万+残り
  if (oku > 0) {
    let result = `${formatWithComma(oku)}億`
    if (man > 0) {
      result += `${formatWithComma(man)}万`
    }
    if (remainder > 0) {
      result += `${formatWithComma(remainder)}`
    }
    return result + '円'
  }

  // 1万以上1億未満: 万+残り
  if (amount >= 10000) {
    let result = `${formatWithComma(man)}万`
    if (remainder > 0) {
      result += `${formatWithComma(remainder)}`
    }
    return result + '円'
  }

  // 1万未満: カンマ区切りのみ
  return `${formatWithComma(amount)}円`
}

// ============================================
// サブコンポーネント
// ============================================

/**
 * 条件行コンポーネント
 * 左右系列（バイナリー）または単一値（ユニレベル）の条件表示に対応
 */
const ConditionRow: React.FC<{ row: ConditionRowData | CultivationRowData }> = ({ row }) => {
  const overallStatus = row.lr ? (row.lr.leftStatus === 'ok' && row.lr.rightStatus === 'ok' ? 'ok' : 'ng') : row.single?.status ?? 'ok'

  return (
    <div className="tc-condition-row">
      <div className={`tc-row-header ${overallStatus === 'ok' ? 'achieved' : 'pending'}`}>
        <span>{row.label}</span>
        <span className={`tc-row-status ${overallStatus}`}>{overallStatus === 'ok' ? '達成' : '未達成'}</span>
      </div>
      <div className="tc-row-body">
        {'requirement' in row && row.requirement && <div className="tc-row-requirement">条件：{row.requirement}</div>}
        {row.lr ? (
          <div className="tc-lr-row">
            <div className="tc-lr-item">
              <div className="tc-lr-label">左系列</div>
              <div className="tc-lr-value">{row.lr.leftValue}</div>
              {row.lr.leftStatus === 'ok' ? <div className="tc-lr-ok">✓ OK</div> : row.lr.leftShortage && <div className="tc-lr-shortage">{row.lr.leftShortage}</div>}
            </div>
            <div className="tc-lr-item tc-lr-item-right">
              <div className="tc-lr-label">右系列</div>
              <div className="tc-lr-value">{row.lr.rightValue}</div>
              {row.lr.rightStatus === 'ok' ? <div className="tc-lr-ok">✓ OK</div> : row.lr.rightShortage && <div className="tc-lr-shortage">{row.lr.rightShortage}</div>}
            </div>
          </div>
        ) : row.single ? (
          <div className="tc-single-value-row">
            <span className="tc-single-label">現在</span>
            <span className="tc-single-value">{row.single.value}</span>
          </div>
        ) : null}
        {row.single && row.single.status === 'ok' && row.single.value !== '—' && <div className="tc-lr-ok" style={{ textAlign: 'right' }}>✓ 条件クリア</div>}
        {row.single && row.single.shortage && <div className="tc-lr-shortage" style={{ textAlign: 'right' }}>{row.single.shortage}</div>}
      </div>
    </div>
  )
}

// ============================================
// メインコンポーネント
// ============================================

const ConditionCheck: React.FC = () => {
  // ----------------------------------------
  // State: UI状態
  // ----------------------------------------
  const [mainTab, setMainTab] = React.useState<MainTab>('promotion')
  const [subTab, setSubTab] = React.useState<SubTab>('binary')
  const [openAccordions, setOpenAccordions] = React.useState<Set<string>>(new Set())

  // ----------------------------------------
  // State: データ
  // ----------------------------------------
  const [positions, setPositions] = React.useState<Position[]>([])
  const [activePositionIndex, setActivePositionIndex] = React.useState(0)
  const [titleConditions, setTitleConditions] = React.useState<TitleCondition[] | null>(null)
  const [binaryData, setBinaryData] = React.useState<BinaryPromotionResponse['response'] | null>(null)
  const [unilevelData, setUnilevelData] = React.useState<UnilevelPromotionResponse['response'] | null>(null)
  const [maintenanceData, setMaintenanceData] = React.useState<MaintenanceConditionResponse['response'] | null>(null)
  const [loading, setLoading] = React.useState(true)

  // ----------------------------------------
  // State: キャッシュ
  // ポジション切替時のAPI再呼び出しを防ぐ
  // ----------------------------------------
  const [conditionCache, setConditionCache] = React.useState<Map<number, {
    binary: BinaryPromotionResponse['response'] | null
    unilevel: UnilevelPromotionResponse['response'] | null
    maintenance: MaintenanceConditionResponse['response'] | null
  }>>(new Map())

  // ----------------------------------------
  // 派生値: タイトル情報
  // ----------------------------------------
  const activePosition = positions[activePositionIndex]

  // 最高タイトル: APIレスポンスを優先（最新データ）
  const maxTitleId = binaryData?.positionTitleId ?? unilevelData?.positionTitleId ?? maintenanceData?.positionTitleId ?? activePosition?.positionTitle ?? 1
  const maxTitleCode = titleIdToCode[maxTitleId] ?? 'SPC'
  const nextTitleId = maxTitleId < 8 ? maxTitleId + 1 : null
  const nextTitleCode = nextTitleId ? titleIdToCode[nextTitleId] : null

  /**
   * 暫定タイトルの計算
   *
   * 現在の実績で達成している最高タイトルを算出。
   * バイナリー/ユニレベルの両方の条件をチェックし、
   * 達成している最高のタイトルIDを返す。
   *
   * 注意: 最高タイトル(maxTitleId)以下にはならない
   * 注意: 3ポジション以上かつメインポジションの場合、最低BCとなる
   */
  const provisionalTitleId = React.useMemo(() => {
    // ★3ポジション以上かつメインポジションの場合の最低タイトルを先に計算
    const isMainPosition = activePosition?.positionNumber === PositionNumber.MainPosition
    const hasThreeOrMorePositions = positions.length >= 3
    const minProvisionalTitleId = isMainPosition && hasThreeOrMorePositions ? TITLE_INFO_MAP.BC.id : 1

    if (!titleConditions || titleConditions.length === 0) {
      // ★早期return時も補正を適用
      return Math.max(maxTitleId, minProvisionalTitleId)
    }

    let achievedTitleId = maxTitleId

    // バイナリー条件の達成判定（GCまでチェック）
    if (binaryData?.conditions) {
      const { singleMonth, consecutiveMonth, training } = binaryData.conditions
      for (let titleId = 8; titleId > maxTitleId; titleId--) {
        const tc = titleConditions.find((t) => t.titleId === titleId)
        if (!tc?.binary) continue

        // 単月条件（count/amountは左右各の値）
        const singleCountReq = tc.binary.single.count ?? 0
        const singleAmountReq = tc.binary.single.amount ?? 0
        const singleCountOk = singleMonth.referralCount.left >= singleCountReq && singleMonth.referralCount.right >= singleCountReq
        const singleAmountOk = singleMonth.totalAmount.left >= singleAmountReq && singleMonth.totalAmount.right >= singleAmountReq
        const singleOk = singleCountOk || singleAmountOk

        // 連月条件
        const consCountReq = tc.binary.consecutive.count ?? 0
        const consAmountReq = tc.binary.consecutive.amount ?? 0
        const consCountOk = consecutiveMonth.referralCount.left >= consCountReq && consecutiveMonth.referralCount.right >= consCountReq
        const consAmountOk = consecutiveMonth.totalAmount.left >= consAmountReq && consecutiveMonth.totalAmount.right >= consAmountReq
        const consOk = consCountOk || consAmountOk

        // 育成条件: 左右合計req.count件以上 かつ 各1件以上
        const trainingOk = tc.binary.training.every((req) => {
          const actual = training.titles.find((t) => t.titleId === req.titleId)
          const leftCount = actual?.leftCount ?? 0
          const rightCount = actual?.rightCount ?? 0
          return leftCount + rightCount >= req.count && leftCount >= 1 && rightCount >= 1
        })

        if ((singleOk || consOk) && trainingOk) {
          achievedTitleId = Math.max(achievedTitleId, titleId)
          break
        }
      }
    }

    // ユニレベル条件の達成判定（GCまでチェック）
    if (unilevelData?.conditions) {
      const { amount, training } = unilevelData.conditions
      for (let titleId = 8; titleId > achievedTitleId; titleId--) {
        const tc = titleConditions.find((t) => t.titleId === titleId)
        if (!tc?.unilevel) continue

        const amountOk = amount >= (tc.unilevel.amountTotal ?? 0)
        const trainingCountOk = tc.unilevel.training.every((tr) => {
          const actual = training.find((t) => t.titleId === tr.titleId)
          return (actual?.count ?? 0) >= tr.count
        })

        let trainingAmountOk = true
        if (tc.unilevel.trainingAmount) {
          const specifiedSeriesAmount = tc.unilevel.training.reduce((sum, tr) => {
            const actual = training.find((t) => t.titleId === tr.titleId)
            return sum + (actual?.seriesAmount ?? 0)
          }, 0)
          const otherSeriesAmount = amount - specifiedSeriesAmount
          trainingAmountOk = otherSeriesAmount >= tc.unilevel.trainingAmount
        }

        if (amountOk && trainingCountOk && trainingAmountOk) {
          achievedTitleId = Math.max(achievedTitleId, titleId)
          break
        }
      }
    }

    // ★最後に補正を適用
    return Math.max(achievedTitleId, minProvisionalTitleId)
  }, [maxTitleId, binaryData?.conditions, unilevelData?.conditions, titleConditions, activePosition?.positionNumber, positions.length])
  const provisionalTitleCode = titleIdToCode[provisionalTitleId] ?? 'SPC'

  /**
   * 維持タイトルの計算
   *
   * 暫定タイトルをベースに、維持条件の達成状況を加味して
   * 実際に維持できるタイトルを算出。
   * 維持条件を満たさない場合は降格する。
   */
  const maintainedTitleId = React.useMemo(() => {
    // 報酬取得タイトルは最高タイトルを上限とする
    const baseTitleId = Math.min(provisionalTitleId, maxTitleId)

    if (!maintenanceData?.amounts || !titleConditions || titleConditions.length === 0) {
      return baseTitleId
    }
    return calculateMaintenanceTitle(baseTitleId, maintenanceData.amounts, titleConditions)
  }, [provisionalTitleId, maxTitleId, maintenanceData?.amounts, titleConditions])
  const maintainedTitleCode = titleIdToCode[maintainedTitleId] ?? 'SPC'

  // ----------------------------------------
  // Effects: データ取得
  // ----------------------------------------

  // アクティブポジション一覧取得（バックエンドでpaymentStatus=Paidのみフィルタ済み）
  React.useEffect(() => {
    const fetchPositions = async () => {
      await activePositionsAPI((response) => {
        setPositions(response.positions)
      })
    }
    fetchPositions()
  }, [])

  // タイトル条件取得
  React.useEffect(() => {
    const fetch = async () => {
      const conditions = await getTitleConditions()
      setTitleConditions(conditions)
    }
    fetch()
  }, [])

  // ポジション別データ取得（キャッシュ対応）
  React.useEffect(() => {
    if (!activePosition) return

    const positionId = activePosition.positionId
    let isCancelled = false

    // キャッシュがあれば使用（API呼び出しをスキップ）
    const cached = conditionCache.get(positionId)
    if (cached) {
      setBinaryData(cached.binary)
      setUnilevelData(cached.unilevel)
      setMaintenanceData(cached.maintenance)
      setLoading(false)
      return
    }

    // キャッシュがなければAPI呼び出し
    const fetchData = async () => {
      setLoading(true)
      const [binary, unilevel, maintenance] = await Promise.all([
        getBinaryPromotionConditions(positionId),
        getUnilevelPromotionConditions(positionId),
        getMaintenanceConditions(positionId),
      ])

      // ポジション切替後は古いレスポンスを破棄
      if (isCancelled) return

      // キャッシュに保存
      setConditionCache((prev) => new Map(prev).set(positionId, { binary, unilevel, maintenance }))

      setBinaryData(binary)
      setUnilevelData(unilevel)
      setMaintenanceData(maintenance)
      setLoading(false)
    }
    fetchData()

    return () => {
      isCancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePosition?.positionId])

  // アコーディオンの開閉状態を初期化
  React.useEffect(() => {
    const newOpenSet = new Set<string>()
    // 未達成（次の目標）はデフォルトで開く
    if (mainTab === 'promotion' && nextTitleCode) {
      newOpenSet.add(`promotion-${subTab}-${nextTitleCode}`)
    }
    if (mainTab === 'maintenance') {
      // 維持タイトルをデフォルトで開く
      newOpenSet.add(`maintenance-${maintainedTitleCode}`)
    }
    setOpenAccordions(newOpenSet)
  }, [mainTab, subTab, maintainedTitleCode, nextTitleCode])

  const toggleAccordion = (id: string) => {
    setOpenAccordions((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      return newSet
    })
  }

  // ----------------------------------------
  // データ構築関数
  // APIレスポンスを表示用データ構造に変換
  // ----------------------------------------

  /**
   * バイナリー昇格条件データを構築
   * 単月/連月の実績と育成条件を含む
   */
  const buildBinaryConditions = (): TitleConditionData[] => {
    if (!binaryData || !titleConditions) return []

    const result: TitleConditionData[] = []
    const { singleMonth, consecutiveMonth, training } = binaryData.conditions

    // 暫定タイトルと次の目標タイトルを表示
    const nextProvisionalTitleId = provisionalTitleId < 8 ? provisionalTitleId + 1 : null
    const targetIds = nextProvisionalTitleId ? [provisionalTitleId, nextProvisionalTitleId] : [provisionalTitleId]

    for (const targetId of targetIds) {
      const tc = titleConditions.find((t) => t.titleId === targetId)
      if (!tc || !tc.binary) continue

      const titleCode = titleIdToCode[targetId]
      const isAchieved = targetId <= provisionalTitleId

      // 単月条件と実績の比較
      const singleCountReq = tc.binary.single.count ?? 0
      const singleCountTotalReq = tc.binary.single.countTotal ?? 0
      const singleAmountReq = tc.binary.single.amount ?? 0
      const singleAmountTotalReq = tc.binary.single.amountTotal ?? 0
      const leftCount = singleMonth.referralCount.left
      const rightCount = singleMonth.referralCount.right
      const leftAmount = singleMonth.totalAmount.left
      const rightAmount = singleMonth.totalAmount.right

      const countLeftOk = leftCount >= singleCountReq
      const countRightOk = rightCount >= singleCountReq
      const amountLeftOk = leftAmount >= singleAmountReq
      const amountRightOk = rightAmount >= singleAmountReq

      // 連月条件と実績の比較
      const consCountReq = tc.binary.consecutive.count ?? 0
      const consCountTotalReq = tc.binary.consecutive.countTotal ?? 0
      const consAmountReq = tc.binary.consecutive.amount ?? 0
      const consAmountTotalReq = tc.binary.consecutive.amountTotal ?? 0
      const consLeftCount = consecutiveMonth.referralCount.left
      const consRightCount = consecutiveMonth.referralCount.right
      const consLeftAmount = consecutiveMonth.totalAmount.left
      const consRightAmount = consecutiveMonth.totalAmount.right

      const consCountLeftOk = consLeftCount >= consCountReq
      const consCountRightOk = consRightCount >= consCountReq
      const consAmountLeftOk = consLeftAmount >= consAmountReq
      const consAmountRightOk = consRightAmount >= consAmountReq

      // グループ（単月実績 + 連月実績）を構築
      const groups: ConditionGroupData[] = [
        {
          title: '単月実績',
          rows: [
            {
              label: 'コネクター紹介',
              requirement: `${singleCountTotalReq}(${singleCountReq})件`,
              lr: {
                leftValue: `${leftCount}件`,
                rightValue: `${rightCount}件`,
                leftStatus: countLeftOk ? 'ok' : 'ng',
                rightStatus: countRightOk ? 'ok' : 'ng',
                leftShortage: countLeftOk ? undefined : `あと${singleCountReq - leftCount}件`,
                rightShortage: countRightOk ? undefined : `あと${singleCountReq - rightCount}件`,
              },
            },
            {
              label: '売上',
              requirement: `${singleAmountTotalReq / 10000}(${singleAmountReq / 10000})万円`,
              lr: {
                leftValue: `${formatAmount(leftAmount)}`,
                rightValue: `${formatAmount(rightAmount)}`,
                leftStatus: amountLeftOk ? 'ok' : 'ng',
                rightStatus: amountRightOk ? 'ok' : 'ng',
                leftShortage: amountLeftOk ? undefined : `あと${formatAmount(singleAmountReq - leftAmount)}`,
                rightShortage: amountRightOk ? undefined : `あと${formatAmount(singleAmountReq - rightAmount)}`,
              },
            },
          ],
        },
      ]

      // 連月実績は2026年2月以降のみ表示
      if (shouldShowConsecutiveMonth()) {
        groups.push({
          title: '連月実績',
          rows: [
            {
              label: 'コネクター紹介',
              requirement: `${consCountTotalReq}(${consCountReq})件`,
              lr: {
                leftValue: `${consLeftCount}件`,
                rightValue: `${consRightCount}件`,
                leftStatus: consCountLeftOk ? 'ok' : 'ng',
                rightStatus: consCountRightOk ? 'ok' : 'ng',
                leftShortage: consCountLeftOk ? undefined : `あと${consCountReq - consLeftCount}件`,
                rightShortage: consCountRightOk ? undefined : `あと${consCountReq - consRightCount}件`,
              },
            },
            {
              label: '売上',
              requirement: `${consAmountTotalReq / 10000}(${consAmountReq / 10000})万円`,
              lr: {
                leftValue: `${formatAmount(consLeftAmount)}`,
                rightValue: `${formatAmount(consRightAmount)}`,
                leftStatus: consAmountLeftOk ? 'ok' : 'ng',
                rightStatus: consAmountRightOk ? 'ok' : 'ng',
                leftShortage: consAmountLeftOk ? undefined : `あと${formatAmount(consAmountReq - consLeftAmount)}`,
                rightShortage: consAmountRightOk ? undefined : `あと${formatAmount(consAmountReq - consRightAmount)}`,
              },
            },
          ],
        })
      }

      result.push({
        titleCode,
        status: isAchieved ? 'achieved' : 'pending',
        groups,
        cultivation: tc.binary.training.length > 0
          ? {
              title: '育成条件',
              rows: tc.binary.training.map((tr) => {
                const actual = training.titles.find((t) => t.titleId === tr.titleId)
                const leftCount = actual?.leftCount ?? 0
                const rightCount = actual?.rightCount ?? 0
                // 判定: 左右合計tr.count件以上 かつ 各1件以上
                const totalOk = leftCount + rightCount >= tr.count
                const leftOk = leftCount >= 1 && totalOk
                const rightOk = rightCount >= 1 && totalOk
                const leftShortage = leftCount < 1 ? 1 - leftCount : 0
                const rightShortage = rightCount < 1 ? 1 - rightCount : 0
                return {
                  label: `${tr.abbreviation}育成`,
                  requirement: `${tr.abbreviation}を${tr.count}系列`,
                  lr: {
                    leftValue: `${tr.abbreviation} ${leftCount}件`,
                    rightValue: `${tr.abbreviation} ${rightCount}件`,
                    leftStatus: leftOk ? 'ok' : 'ng',
                    rightStatus: rightOk ? 'ok' : 'ng',
                    leftShortage: leftOk ? undefined : `あと${leftShortage}件`,
                    rightShortage: rightOk ? undefined : `あと${rightShortage}件`,
                  },
                }
              }),
            }
          : { title: '育成条件', rows: [{ label: '条件なし', single: { value: '—', status: 'ok' } }] },
      })
    }

    return result
  }

  /**
   * ユニレベル昇格条件データを構築
   * 累積課金額と育成条件（系列数・他系列売上）を含む
   *
   * 注意: ユニレベルはIC(titleId=4)以上のタイトルのみ対象
   * SPC/FC/BCユーザーにはICの条件を参考表示
   */
  const buildUnilevelConditions = (): TitleConditionData[] => {
    if (!unilevelData || !titleConditions) return []

    const result: TitleConditionData[] = []
    const { amount: actualAmount, training } = unilevelData.conditions

    // ユニレベル対象タイトルを決定（暫定タイトルベース）
    let targetIds: number[]
    const nextProvisionalTitleId = provisionalTitleId < 8 ? provisionalTitleId + 1 : null
    if (provisionalTitleId < FIRST_UNILEVEL_TITLE_ID) {
      // SPC/FC/BC: IC(4)を最初の目標として表示
      targetIds = [FIRST_UNILEVEL_TITLE_ID]
    } else if (provisionalTitleId >= 8) {
      // GC: 現在のタイトルのみ
      targetIds = [provisionalTitleId]
    } else {
      // IC以上: 暫定タイトルと次のタイトル
      targetIds = nextProvisionalTitleId ? [provisionalTitleId, nextProvisionalTitleId] : [provisionalTitleId]
    }

    for (const targetId of targetIds) {
      const tc = titleConditions.find((t) => t.titleId === targetId)
      if (!tc || !tc.unilevel) continue

      const titleCode = titleIdToCode[targetId]
      const requiredAmount = tc.unilevel.amountTotal ?? 0
      const amountOk = actualAmount >= requiredAmount

      // 育成条件の達成判定（系列数）
      const trainingCountOk = tc.unilevel.training.every((tr) => {
        const actual = training.find((t) => t.titleId === tr.titleId)
        return (actual?.count ?? 0) >= tr.count
      })

      // 他系列売上累計条件の達成判定
      let trainingAmountOk = true
      let otherSeriesAmount = 0
      if (tc.unilevel.trainingAmount) {
        const specifiedSeriesAmount = tc.unilevel.training.reduce((sum, tr) => {
          const actual = training.find((t) => t.titleId === tr.titleId)
          return sum + (actual?.seriesAmount ?? 0)
        }, 0)
        otherSeriesAmount = actualAmount - specifiedSeriesAmount
        trainingAmountOk = otherSeriesAmount >= tc.unilevel.trainingAmount
      }

      // 全条件を満たしているか（実績ベースの判定）
      const isConditionMet = amountOk && trainingCountOk && trainingAmountOk
      // 既に達成済み（maxTitleIdがtargetId以上）または条件を満たしている場合は達成
      const isAchieved = targetId <= maxTitleId || isConditionMet

      const conditionData: TitleConditionData = {
        titleCode,
        status: isAchieved ? 'achieved' : 'pending',
        groups: [
          {
            title: '累積実績',
            rows: [
              {
                label: '累積課金額',
                requirement: `${formatAmount(requiredAmount)}`,
                single: {
                  value: `${formatAmount(actualAmount)}`,
                  status: amountOk ? 'ok' : 'ng',
                  shortage: amountOk ? undefined : `あと${formatAmount(requiredAmount - actualAmount)}`,
                },
              },
            ],
          },
        ],
        cultivation: (() => {
          if (tc.unilevel.training.length === 0 && !tc.unilevel.trainingAmount) {
            return { title: '育成条件', rows: [{ label: '条件なし', single: { value: '—', status: 'ok' as const } }] }
          }
          const cultivationRows: CultivationRowData[] = []
          // 系列数条件
          tc.unilevel.training.forEach((tr) => {
            const actual = training.find((t) => t.titleId === tr.titleId)
            const countOk = (actual?.count ?? 0) >= tr.count
            cultivationRows.push({
              label: `${tr.abbreviation}育成`,
              requirement: `${tr.abbreviation}を${tr.count}系列`,
              single: {
                value: `${tr.abbreviation} ${actual?.count ?? 0}系列`,
                status: countOk ? 'ok' : 'ng',
                shortage: countOk ? undefined : `あと${tr.count - (actual?.count ?? 0)}系列`,
              },
            })
          })
          // 他系列売上累計条件
          if (tc.unilevel.trainingAmount) {
            cultivationRows.push({
              label: '他系列売上累計',
              requirement: `${formatAmount(tc.unilevel.trainingAmount)}以上`,
              single: {
                value: `${formatAmount(otherSeriesAmount)}`,
                status: trainingAmountOk ? 'ok' : 'ng',
                shortage: trainingAmountOk ? undefined : `あと${formatAmount(tc.unilevel.trainingAmount - otherSeriesAmount)}`,
              },
            })
          }
          return { title: '育成条件', rows: cultivationRows }
        })(),
      }

      // SPC/FC/BCユーザー（暫定タイトルもIC未満）の場合、注記を追加
      if (provisionalTitleId < FIRST_UNILEVEL_TITLE_ID && targetId === FIRST_UNILEVEL_TITLE_ID) {
        conditionData.note = '※ 累積ルートはIC以上のタイトルが対象です。FC・BCは単月/連月ルートで昇格してください。'
      }

      result.push(conditionData)
    }

    return result
  }

  /**
   * 維持条件データを構築（全タイトル分）
   * GC(8)からSPC(1)まで全タイトルの維持条件を返す
   *
   * ステータス判定:
   * - 維持タイトルと同じ: achieved/pending
   * - 維持タイトルより上: neutral（取得不可）
   * - 維持タイトルより下: achieved
   */
  const buildMaintenanceConditions = (): TitleConditionData[] => {
    if (!maintenanceData || !titleConditions) return []

    const { amounts } = maintenanceData
    const leftAmount = amounts.left
    const rightAmount = amounts.right

    const result: TitleConditionData[] = []

    // GC(8)からSPC(1)まで全タイトルを表示
    for (let titleId = 8; titleId >= 1; titleId--) {
      const tc = titleConditions.find((t) => t.titleId === titleId)
      const titleCode = titleIdToCode[titleId]
      if (!titleCode) continue

      const req = tc?.maintenance?.amount ?? 0
      const reqTotal = tc?.maintenance?.amountTotal ?? 0
      const leftOk = leftAmount >= req
      const rightOk = rightAmount >= req
      const isConditionMet = leftOk && rightOk

      // ステータス判定: 条件の達成状況で判定
      const status: ConditionStatus = isConditionMet ? 'achieved' : 'pending'

      result.push({
        titleCode,
        status,
        groups: [
          {
            title: '維持条件',
            rows: req > 0
              ? [
                  {
                    label: '課金額',
                    requirement: `${reqTotal / 10000}(${req / 10000})万円`,
                    lr: {
                      leftValue: `${formatAmount(leftAmount)}`,
                      rightValue: `${formatAmount(rightAmount)}`,
                      leftStatus: leftOk ? 'ok' : 'ng',
                      rightStatus: rightOk ? 'ok' : 'ng',
                      leftShortage: leftOk ? undefined : `あと${formatAmount(req - leftAmount)}`,
                      rightShortage: rightOk ? undefined : `あと${formatAmount(req - rightAmount)}`,
                    },
                  },
                ]
              : [{ label: '維持条件', requirement: 'なし', single: { value: '—', status: 'ok' } }],
          },
        ],
      })
    }

    return result
  }

  // ----------------------------------------
  // ヘルパー関数
  // ----------------------------------------

  /** アコーディオンヘッダーに表示するサマリー値を取得 */
  const getSummaryValues = (condition: TitleConditionData) => {
    const summaryItems: { label: string; value: string; ok: boolean }[] = []

    condition.groups.forEach((group) => {
      if (group.title === '単月実績' || group.title === '累積実績') {
        group.rows.forEach((row) => {
          if (row.lr) {
            if (row.label === 'コネクター紹介') {
              const leftNum = parseInt(row.lr.leftValue)
              const rightNum = parseInt(row.lr.rightValue)
              summaryItems.push({
                label: '紹介',
                value: `${leftNum + rightNum}件`,
                ok: row.lr.leftStatus === 'ok' && row.lr.rightStatus === 'ok',
              })
            } else if (row.label === '売上') {
              // フォーマット済み文字列から数値を取得して足し合わせ
              const leftAmount = parseFormattedAmount(row.lr.leftValue)
              const rightAmount = parseFormattedAmount(row.lr.rightValue)
              const totalAmount = leftAmount + rightAmount
              summaryItems.push({
                label: '売上',
                value: formatAmount(totalAmount),
                ok: row.lr.leftStatus === 'ok' && row.lr.rightStatus === 'ok',
              })
            }
          } else if (row.single && row.label === '累積課金額') {
            summaryItems.push({
              label: '累積',
              value: row.single.value,
              ok: row.single.status === 'ok',
            })
          }
        })
      }
    })

    return summaryItems
  }

  // ----------------------------------------
  // レンダリング関数
  // ----------------------------------------

  /** アコーディオン内のコンテンツをレンダリング */
  const renderAccordionContent = (condition: TitleConditionData) => (
    <>
      {condition.groups.map((group, groupIndex) => (
        <div className="tc-section" key={groupIndex}>
          <div className="tc-section-title">{group.title}</div>
          {group.rows.map((row, rowIndex) => (
            <ConditionRow row={row} key={rowIndex} />
          ))}
        </div>
      ))}

      {condition.cultivation && (
        <div className="tc-cultivation-section">
          <div className="tc-cultivation-title">{condition.cultivation.title}</div>
          {condition.cultivation.rows.map((row, rowIndex) =>
            row.single?.value === '—' && row.label === '条件なし' ? (
              <div key={rowIndex} style={{ fontSize: '13px', color: '#111' }}>
                条件なし
              </div>
            ) : (
              <ConditionRow row={row} key={rowIndex} />
            )
          )}
        </div>
      )}

      {condition.note && <div className="tc-note-box warning">{condition.note}</div>}
    </>
  )

  /** 昇格条件タブのコンテンツをレンダリング */
  const renderPromotionContent = () => {
    const conditions = subTab === 'binary' ? buildBinaryConditions() : buildUnilevelConditions()

    if (loading || conditions.length === 0) {
      return (
        <div className="tc-content">
          <Card body className="tc-info-card">
            <div className="tc-info-row">
              <span className="tc-info-label">コネクターID</span>
              <span className="tc-info-value">{activePosition?.positionId ?? '-'}</span>
            </div>
          </Card>
          <div className="text-center py-4 text-muted">{loading ? '読み込み中...' : 'データがありません'}</div>
        </div>
      )
    }

    return (
      <div className="tc-content">
        {/* 基本情報 */}
        <Card body className="tc-info-card">
          <div className="tc-info-row">
            <span className="tc-info-label">コネクターID</span>
            <span className="tc-info-value">{activePosition?.positionId}</span>
          </div>
          <div className="tc-info-divider" />
          <div className="tc-info-row">
            <span className="tc-info-label">最高タイトル</span>
            <Badge bg="none" className={`tc-title-badge ${titleInfoMap[maxTitleCode].className}`}>
              {maxTitleCode}
            </Badge>
          </div>
          <div className="tc-info-divider" />
          <div className="tc-info-row">
            <span className="tc-info-label">暫定タイトル</span>
            <Badge bg="none" className={`tc-title-badge ${titleInfoMap[provisionalTitleCode].className}`}>
              {provisionalTitleCode}
            </Badge>
          </div>
          <div className="tc-info-note">
            ※暫定タイトルは現在の実績で達成している最高タイトルです。実際に確定するタイトルとは異なる場合があります。
          </div>
        </Card>

        {/* バイナリー/ユニレベル切替 */}
        <div className="tc-sub-tabs">
          <button className={`tc-sub-tab-btn ${subTab === 'binary' ? 'active' : ''}`} onClick={() => setSubTab('binary')}>
            バイナリー
          </button>
          <button className={`tc-sub-tab-btn ${subTab === 'unilevel' ? 'active' : ''}`} onClick={() => setSubTab('unilevel')}>
            ユニレベル
          </button>
        </div>

        {/* アコーディオン */}
        {conditions.map((cond) => {
          const accordionId = `promotion-${subTab}-${cond.titleCode}`
          const isOpen = openAccordions.has(accordionId)
          const titleInfo = titleInfoMap[cond.titleCode]
          const statusClass = cond.status === 'achieved' ? 'achieved' : cond.status === 'pending' ? 'pending' : 'neutral'
          const statusLabel = cond.status === 'achieved' ? '達成' : '未達成'
          const summaryValues = getSummaryValues(cond)

          return (
            <div className={`tc-accordion ${isOpen ? 'open' : ''}`} key={accordionId}>
              <div className={`tc-accordion-header ${statusClass}`} onClick={() => toggleAccordion(accordionId)}>
                <div className="tc-accordion-header-left">
                  <Badge bg="none" className={`tc-title-badge small ${titleInfo.className}`}>
                    {titleInfo.code}
                  </Badge>
                  <div>
                    <div className="tc-accordion-title">{titleInfo.name}</div>
                    {cond.status === 'pending' ? (
                      <div className="tc-accordion-subtitle">次の目標タイトル</div>
                    ) : (
                      !isOpen &&
                      summaryValues.length > 0 && (
                        <div className="tc-summary-row">
                          {summaryValues.map((sv, idx) => (
                            <span key={idx} className={`tc-summary-item ${sv.ok ? 'ok' : 'ng'}`}>
                              {sv.label}: <span className="value">{sv.value}</span>
                            </span>
                          ))}
                        </div>
                      )
                    )}
                  </div>
                </div>
                <div className="tc-accordion-header-right">
                  <span className={`tc-status-badge ${statusClass}`}>{statusLabel}</span>
                  <span className="tc-accordion-icon">&#9660;</span>
                </div>
              </div>
              <div className="tc-accordion-body">{renderAccordionContent(cond)}</div>
            </div>
          )
        })}

        {/* 注釈 */}
        <div className="tc-note-box">
          <div className="tc-note-item">※ ポイント課金情報は5分前時点のデータを表示しています</div>
          <div className="tc-note-item">※ 紹介者件数及びディール購入額は最新のデータを表示しています</div>
        </div>
      </div>
    )
  }

  /** 維持条件タブのコンテンツをレンダリング */
  const renderMaintenanceContent = () => {
    const conditions = buildMaintenanceConditions()

    if (loading || conditions.length === 0) {
      return (
        <div className="tc-content">
          <Card body className="tc-info-card">
            <div className="tc-info-row">
              <span className="tc-info-label">コネクターID</span>
              <span className="tc-info-value">{activePosition?.positionId ?? '-'}</span>
            </div>
          </Card>
          <div className="text-center py-4 text-muted">{loading ? '読み込み中...' : 'データがありません'}</div>
        </div>
      )
    }

    return (
      <div className="tc-content">
        {/* 基本情報 */}
        <Card body className="tc-info-card">
          <div className="tc-info-row">
            <span className="tc-info-label">コネクターID</span>
            <span className="tc-info-value">{activePosition?.positionId}</span>
          </div>
          <div className="tc-info-divider" />
          <div className="tc-info-row">
            <span className="tc-info-label">最高タイトル</span>
            <Badge bg="none" className={`tc-title-badge ${titleInfoMap[maxTitleCode].className}`}>
              {maxTitleCode}
            </Badge>
          </div>
          <div className="tc-info-divider" />
          <div className="tc-info-row">
            <span className="tc-info-label">暫定タイトル</span>
            <Badge bg="none" className={`tc-title-badge ${titleInfoMap[provisionalTitleCode].className}`}>
              {provisionalTitleCode}
            </Badge>
          </div>
          <div className="tc-info-divider" />
          <div className="tc-info-row">
            <span className="tc-info-label">報酬取得タイトル</span>
            <Badge bg="none" className={`tc-title-badge ${titleInfoMap[maintainedTitleCode].className}`}>
              {maintainedTitleCode}
            </Badge>
          </div>
          <div className="tc-info-note">
            ※実際に支払われる報酬のタイトルとは異なる場合があります。
          </div>
        </Card>

        {/* アコーディオン（全タイトル表示） */}
        {conditions.map((cond) => {
          const accordionId = `maintenance-${cond.titleCode}`
          const isOpen = openAccordions.has(accordionId)
          const titleInfo = titleInfoMap[cond.titleCode]
          const titleId = titleInfo.id
          const isCurrent = titleId === maintainedTitleId
          const statusClass = cond.status === 'achieved' ? 'achieved' : 'pending'

          // ステータスラベル: 報酬取得タイトルは「維持中」表示
          const statusLabel = isCurrent ? '維持中' : cond.status === 'achieved' ? '達成' : '未達成'

          // サブタイトル: 報酬取得タイトルのみ表示
          const subtitle = isCurrent ? '報酬取得タイトル' : ''

          return (
            <div className={`tc-accordion ${isOpen ? 'open' : ''}`} key={accordionId}>
              <div className={`tc-accordion-header ${statusClass}`} onClick={() => toggleAccordion(accordionId)}>
                <div className="tc-accordion-header-left">
                  <Badge bg="none" className={`tc-title-badge small ${titleInfo.className}`}>
                    {titleInfo.code}
                  </Badge>
                  <div>
                    <div className="tc-accordion-title">{titleInfo.name}</div>
                    {subtitle && <div className="tc-accordion-subtitle">{subtitle}</div>}
                  </div>
                </div>
                <div className="tc-accordion-header-right">
                  <span className={`tc-status-badge ${statusClass}`}>{statusLabel}</span>
                  <span className="tc-accordion-icon">&#9660;</span>
                </div>
              </div>
              <div className="tc-accordion-body">{renderAccordionContent(cond)}</div>
            </div>
          )
        })}

        {/* 注釈 */}
        <div className="tc-note-box">
          <div className="tc-note-item">※ ポイント課金情報は5分前時点のデータを表示しています</div>
          <div className="tc-note-item">※ 紹介者件数及びディール購入額は最新のデータを表示しています</div>
        </div>
      </div>
    )
  }

  // ----------------------------------------
  // JSX: メインレンダリング
  // ----------------------------------------

  return (
    <>
      <Header title="条件確認" />
      <main>
        <div className="tc-container">
          {/* メインタブ */}
          <div className="tc-main-tabs">
            <button className={`tc-main-tab-btn ${mainTab === 'promotion' ? 'active' : ''}`} onClick={() => setMainTab('promotion')}>
              昇格条件
            </button>
            <button className={`tc-main-tab-btn ${mainTab === 'maintenance' ? 'active' : ''}`} onClick={() => setMainTab('maintenance')}>
              維持条件
            </button>
          </div>

          {/* ポジションタブ */}
          <div className="tc-position-tabs">
            {positions.map((position, index) => (
              <button
                key={position.positionId}
                className={`tc-position-tab-btn ${position.positionNumber === PositionNumber.MainPosition ? 'main-position' : ''} ${activePositionIndex === index ? 'active' : ''}`}
                onClick={() => setActivePositionIndex(index)}
              >
                <Badge bg="none" className={`tc-position-badge ${position.positionNumber === PositionNumber.MainPosition ? 'main' : 'sub'}`}>
                  {position.positionNumber === PositionNumber.MainPosition ? 'メイン' : `サブ${position.positionNumber - 1}`}
                </Badge>
                <span className="tc-position-id">ID: {position.positionId}</span>
              </button>
            ))}
          </div>

          {/* コンテンツ */}
          {mainTab === 'promotion' ? renderPromotionContent() : renderMaintenanceContent()}
        </div>
      </main>
      <Footer />

      <style>{`
        /* コンテナ */
        .tc-container {
          max-width: 100%;
          background-color: #fff;
          margin-top: -24px;
        }

        /* メインタブ */
        .tc-main-tabs {
          display: flex;
          background-color: #f5f5f5;
          padding: 8px;
          gap: 8px;
          border-bottom: 2px solid #e0e0e0;
        }
        .tc-main-tab-btn {
          flex: 1;
          padding: 14px 0;
          background-color: #fff;
          border: 2px solid #d0d0d0;
          border-radius: 10px;
          cursor: pointer;
          font-size: 15px;
          color: #555;
          font-weight: 600;
          transition: all 0.2s ease;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
        }
        .tc-main-tab-btn:hover {
          background-color: #f0f0f0;
          border-color: #bbb;
        }
        .tc-main-tab-btn.active {
          color: #fff;
          background: linear-gradient(135deg, #0066cc 0%, #0052a3 100%);
          border-color: #0052a3;
          box-shadow: 0 4px 12px rgba(0, 102, 204, 0.3);
          transform: translateY(-1px);
        }
        .tc-main-tab-btn.active::after {
          content: "▼";
          display: block;
          font-size: 8px;
          margin-top: 4px;
          opacity: 0.8;
        }

        /* ポジションタブ */
        .tc-position-tabs {
          display: flex;
          overflow-x: auto;
          background-color: #f9f9f9;
          border-bottom: 2px solid #e8e8e8;
          scrollbar-width: none;
        }
        .tc-position-tabs::-webkit-scrollbar {
          display: none;
        }
        .tc-position-tab-btn {
          flex: 0 0 auto;
          min-width: 90px;
          padding: 14px 12px;
          background-color: transparent;
          border: 1px solid #e0e0e0;
          border-bottom: 3px solid transparent;
          cursor: pointer;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 6px;
          transition: all 0.2s ease;
        }
        .tc-position-tab-btn.main-position {
          background-color: #e3f2fd;
        }
        .tc-position-tab-btn.active {
          background-color: #fff;
          border-bottom-color: #0066cc;
        }
        .tc-position-badge {
          padding: 6px 12px;
          font-size: 10px;
          font-weight: 700;
          border-radius: 12px;
        }
        .tc-position-badge.main {
          color: #fff;
          background-color: #0a75ba;
        }
        .tc-position-badge.sub {
          color: #494949;
          background-color: #aad4ee;
        }
        .tc-position-id {
          font-size: 12px;
          color: #999;
        }
        .tc-position-tab-btn.active .tc-position-id {
          color: #0a75ba;
          font-weight: 700;
        }

        /* コンテンツエリア */
        .tc-content {
          padding: 16px;
          background-color: #f0f2f5;
        }

        /* 基本情報カード */
        .tc-info-card {
          background-color: #fff;
          border-radius: 8px;
          padding: 16px;
          margin-bottom: 16px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
          border: none;
        }
        .tc-info-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 8px 0;
        }
        .tc-info-label {
          font-size: 13px;
          color: #666;
        }
        .tc-info-value {
          font-weight: 600;
        }
        .tc-info-divider {
          height: 1px;
          background-color: #eee;
          margin: 4px 0;
        }
        .tc-info-note {
          font-size: 11px;
          color: #888;
          margin-top: 12px;
          line-height: 1.5;
        }

        /* タイトルバッジ */
        .tc-title-badge {
          padding: 6px 14px;
          border-radius: 20px;
          color: #fff;
          font-size: 11px;
          font-weight: 700;
          display: inline-block;
        }
        .tc-title-badge.small {
          padding: 4px 10px;
          font-size: 10px;
        }
        .tc-title-gc {
          background: linear-gradient(135deg, #9333ea 0%, #ec4899 100%);
        }
        .tc-title-snc {
          background: linear-gradient(135deg, #2563eb 0%, #06b6d4 100%);
        }
        .tc-title-nc {
          background: linear-gradient(135deg, #4f46e5 0%, #9333ea 100%);
        }
        .tc-title-slc {
          background: linear-gradient(135deg, #eab308 0%, #f97316 100%);
        }
        .tc-title-ic {
          background: linear-gradient(135deg, #dc2626 0%, #f97316 100%);
        }
        .tc-title-bc {
          background: linear-gradient(135deg, #f97316 0%, #dc2626 100%);
        }
        .tc-title-fc {
          background: linear-gradient(135deg, #ef4444 0%, #ec4899 100%);
        }
        .tc-title-spc {
          background: linear-gradient(135deg, #6b7280 0%, #4b5563 100%);
        }

        /* バイナリー/ユニレベル サブタブ */
        .tc-sub-tabs {
          display: flex;
          background-color: #fff;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
          margin-bottom: 16px;
        }
        .tc-sub-tab-btn {
          flex: 1;
          padding: 12px;
          border: none;
          cursor: pointer;
          font-size: 13px;
          font-weight: 600;
          background-color: #fff;
          color: #0066cc;
          transition: all 0.2s;
        }
        .tc-sub-tab-btn.active {
          background-color: #0066cc;
          color: #fff;
        }

        /* アコーディオン */
        .tc-accordion {
          margin-bottom: 12px;
          border-radius: 10px;
          overflow: hidden;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
        }
        .tc-accordion-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 14px 16px;
          background-color: #fff;
          cursor: pointer;
          transition: background-color 0.2s;
          border-left: 4px solid #e5e7eb;
        }
        .tc-accordion-header:hover {
          background-color: #f9fafb;
        }
        .tc-accordion-header.achieved {
          border-left-color: #10b981;
          background: linear-gradient(to right, #f0fdf4 0%, #fff 30%);
        }
        .tc-accordion-header.pending {
          border-left-color: #f59e0b;
          background: linear-gradient(to right, #fffbeb 0%, #fff 30%);
        }
        .tc-accordion-header.neutral {
          border-left-color: #6b7280;
          background: linear-gradient(to right, #f3f4f6 0%, #fff 30%);
        }
        .tc-accordion-header-left {
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .tc-accordion-title {
          font-size: 14px;
          font-weight: 600;
          color: #111;
        }
        .tc-accordion-subtitle {
          font-size: 11px;
          color: #666;
          margin-top: 2px;
        }
        .tc-accordion-header-right {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .tc-accordion-icon {
          font-size: 12px;
          color: #999;
          transition: transform 0.3s;
        }
        .tc-accordion.open .tc-accordion-icon {
          transform: rotate(180deg);
        }
        .tc-accordion-body {
          display: none;
          padding: 16px;
          background-color: #fff;
          border-top: 1px solid #eee;
        }
        .tc-accordion.open .tc-accordion-body {
          display: block;
        }

        /* サマリー行 */
        .tc-summary-row {
          display: flex;
          gap: 12px;
          margin-top: 6px;
          flex-wrap: wrap;
        }
        .tc-summary-item {
          font-size: 11px;
          color: #666;
        }
        .tc-summary-item .value {
          font-weight: 600;
          color: #111;
        }
        .tc-summary-item.ok .value {
          color: #059669;
        }
        .tc-summary-item.ng .value {
          color: #dc2626;
        }

        /* ステータスバッジ */
        .tc-status-badge {
          font-size: 10px;
          padding: 4px 10px;
          border-radius: 12px;
          font-weight: 700;
        }
        .tc-status-badge.achieved {
          background-color: #d1fae5;
          color: #065f46;
        }
        .tc-status-badge.pending {
          background-color: #fee2e2;
          color: #991b1b;
        }
        .tc-status-badge.neutral {
          background-color: #e5e7eb;
          color: #4b5563;
        }

        /* セクション */
        .tc-section {
          margin-bottom: 16px;
        }
        .tc-section-title {
          font-size: 12px;
          font-weight: 700;
          color: #6b7280;
          margin-bottom: 10px;
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .tc-section-title::before {
          content: "";
          width: 3px;
          height: 14px;
          background-color: #0066cc;
          border-radius: 2px;
        }

        /* 育成条件セクション */
        .tc-cultivation-section {
          padding: 14px;
          background-color: #f0f9ff;
          border-radius: 8px;
          border-left: 4px solid #0066cc;
        }
        .tc-cultivation-title {
          font-size: 12px;
          font-weight: 700;
          color: #0066cc;
          margin-bottom: 10px;
        }

        /* 条件行 */
        .tc-condition-row {
          margin-bottom: 10px;
          border-radius: 8px;
          overflow: hidden;
          border: 1px solid #e5e7eb;
          background-color: #fff;
        }
        .tc-row-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 10px 12px;
          background-color: #f9fafb;
          font-size: 13px;
          font-weight: 600;
        }
        .tc-row-header.achieved {
          background-color: #ecfdf5;
          color: #166534;
        }
        .tc-row-header.pending {
          background-color: #fff7ed;
          color: #9a3412;
        }
        .tc-row-status {
          font-size: 10px;
          padding: 3px 8px;
          border-radius: 10px;
          font-weight: 600;
        }
        .tc-row-status.ok {
          background-color: #d1fae5;
          color: #065f46;
        }
        .tc-row-status.ng {
          background-color: #fee2e2;
          color: #991b1b;
        }
        .tc-row-body {
          padding: 12px;
        }
        .tc-row-requirement {
          font-size: 11px;
          color: #6b7280;
          margin-bottom: 10px;
          padding-bottom: 8px;
          border-bottom: 1px dashed #e5e7eb;
        }
        .tc-lr-row {
          display: flex;
        }
        .tc-lr-item {
          flex: 1;
          text-align: center;
          padding: 8px;
        }
        .tc-lr-item-right {
          border-left: 1px solid #e5e7eb;
        }
        .tc-lr-label {
          font-size: 11px;
          color: #6b7280;
          margin-bottom: 4px;
        }
        .tc-lr-value {
          font-size: 16px;
          font-weight: 700;
          color: #111827;
        }
        .tc-lr-shortage {
          font-size: 11px;
          color: #dc2626;
          font-weight: 600;
          margin-top: 4px;
        }
        .tc-lr-ok {
          font-size: 11px;
          color: #059669;
          font-weight: 600;
          margin-top: 4px;
        }

        /* 単一値表示 */
        .tc-single-value-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 8px 0;
        }
        .tc-single-label {
          font-size: 12px;
          color: #6b7280;
        }
        .tc-single-value {
          font-size: 15px;
          font-weight: 700;
          color: #111827;
        }

        /* 注記 */
        .tc-note-box {
          font-size: 11px;
          color: #6b7280;
          padding: 12px;
          background-color: #f9fafb;
          border-radius: 6px;
          margin-top: 12px;
        }
        .tc-note-box.warning {
          background-color: #fef3c7;
          color: #92400e;
          border-left: 3px solid #f59e0b;
        }
        .tc-note-item {
          margin-bottom: 4px;
        }
        .tc-note-item:last-child {
          margin-bottom: 0;
        }
      `}</style>
    </>
  )
}

export default ConditionCheck
